/**
 * 统一认证弹窗组件
 * 支持：登录、注册、短信登录、找回账号、添加已有账号
 */
import React, { useState, useEffect, useCallback } from 'react'
import { 
  X, User, Phone, ArrowLeft, 
  Loader2, Check, AlertCircle, Copy, Smartphone
} from 'lucide-react'
import {
  registerUser,
  loginUser,
  sendSmsCode,
  loginWithSms,
  findUuidByPhone,
  checkUsername,
  forgotAccount,
  verifyUser
} from '../../services/api'

// 认证模式
type AuthMode = 
  | 'login'           // 用户名/手机号+验证码登录
  | 'register'        // 注册新账号（用户名+手机号+验证码）
  | 'sms-login'       // 短信验证码登录（自动注册）
  | 'find-uuid'       // 通过手机号找回UUID
  | 'add-existing'    // 添加已有账号（输入UUID）
  | 'forgot-account'   // 找回账号

interface AuthDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (result: {
    userUuid: string
    username?: string
    nickname?: string
    token?: string
    refreshToken?: string
    isNewUser?: boolean
  }) => void
  initialMode?: AuthMode
  title?: string
}

export const AuthDialog: React.FC<AuthDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialMode = 'login',
  title
}) => {
  const [mode, setMode] = useState<AuthMode>(initialMode)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // 表单字段
  const [username, setUsername] = useState('')
  const [phone, setPhone] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [nickname, setNickname] = useState('')
  const [uuid, setUuid] = useState('')
  
  // UI 状态
  const [countdown, setCountdown] = useState(0)
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [usernameChecking, setUsernameChecking] = useState(false)
  
  // 找回结果
  const [foundAccount, setFoundAccount] = useState<{
    userUuid: string
    username: string
    nickname?: string
  } | null>(null)

  // 重置表单
  const resetForm = useCallback(() => {
    setUsername('')
    setPhone('')
    setSmsCode('')
    setNickname('')
    setUuid('')
    setError(null)
    setSuccess(null)
    setUsernameAvailable(null)
    setFoundAccount(null)
  }, [])

  // 切换模式时重置表单
  useEffect(() => {
    resetForm()
  }, [mode, resetForm])

  // 重置初始模式
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode)
      resetForm()
    }
  }, [isOpen, initialMode, resetForm])

  // 倒计时
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // 检查用户名可用性（防抖）
  useEffect(() => {
    if (mode !== 'register' || !username || username.length < 3) {
      setUsernameAvailable(null)
      return
    }

    const timer = setTimeout(async () => {
      setUsernameChecking(true)
      const result = await checkUsername(username)
      setUsernameChecking(false)
      if (result.success && result.data) {
        setUsernameAvailable(result.data.available)
        if (!result.data.available && result.data.reason) {
          setError(result.data.reason)
        } else {
          setError(null)
        }
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [username, mode])

  // 发送验证码
  const handleSendSms = async () => {
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入正确的手机号')
      return
    }

    setIsLoading(true)
    setError(null)

    const smsType = mode === 'find-uuid' || mode === 'forgot-account' ? 'find_uuid' 
      : mode === 'register' ? 'register'
      : 'login'

    const result = await sendSmsCode({ phone, type: smsType })
    setIsLoading(false)

    if (result.success) {
      setCountdown(60)
      setSuccess('验证码已发送')
      // 开发环境显示验证码
      if (result.data?.code) {
        setSuccess(`验证码已发送: ${result.data.code}`)
      }
      setTimeout(() => setSuccess(null), 3000)
    } else {
      setError(result.message)
    }
  }

  // 用户名/手机号+验证码登录
  const handleLogin = async () => {
    if (!username || !smsCode) {
      setError('请填写用户名/手机号和验证码')
      return
    }

    setIsLoading(true)
    setError(null)

    const result = await loginUser({ identifier: username, code: smsCode })
    setIsLoading(false)

    if (result.success && result.data) {
      onSuccess({
        userUuid: result.data.user.userUuid,
        username: result.data.user.username,
        nickname: result.data.user.nickname,
        token: result.data.token,
        refreshToken: result.data.refreshToken
      })
    } else {
      setError(result.message)
    }
  }

  // 注册（用户名+手机号+验证码）
  const handleRegister = async () => {
    if (!username || !phone || !smsCode) {
      setError('请填写用户名、手机号和验证码')
      return
    }

    if (usernameAvailable === false) {
      setError('用户名不可用')
      return
    }

    setIsLoading(true)
    setError(null)

    const result = await registerUser({
      username,
      phone,
      code: smsCode,
      nickname: nickname || undefined
    })
    setIsLoading(false)

    if (result.success && result.data) {
      onSuccess({
        userUuid: result.data.user.userUuid,
        username: result.data.user.username,
        nickname: result.data.user.nickname,
        token: result.data.token,
        refreshToken: result.data.refreshToken,
        isNewUser: true
      })
    } else {
      setError(result.message)
    }
  }

  // 短信登录
  const handleSmsLogin = async () => {
    if (!phone || !smsCode) {
      setError('请填写手机号和验证码')
      return
    }

    setIsLoading(true)
    setError(null)

    const result = await loginWithSms({ phone, code: smsCode })
    setIsLoading(false)

    if (result.success && result.data) {
      onSuccess({
        userUuid: result.data.user.userUuid,
        username: result.data.user.username,
        nickname: result.data.user.nickname,
        token: result.data.token,
        refreshToken: result.data.refreshToken,
        isNewUser: result.data.isNewUser
      })
    } else {
      setError(result.message)
    }
  }

  // 找回UUID
  const handleFindUuid = async () => {
    if (!phone || !smsCode) {
      setError('请填写手机号和验证码')
      return
    }

    setIsLoading(true)
    setError(null)

    const result = await findUuidByPhone({ phone, code: smsCode })
    setIsLoading(false)

    if (result.success && result.data) {
      setFoundAccount(result.data)
      setSuccess('找回成功！')
    } else {
      setError(result.message)
    }
  }

  // 使用找回的账号
  const handleUseFoundAccount = () => {
    if (foundAccount) {
      onSuccess({
        userUuid: foundAccount.userUuid,
        username: foundAccount.username,
        nickname: foundAccount.nickname
      })
    }
  }

  // 添加已有账号（通过UUID）
  const handleAddExisting = async () => {
    if (!uuid) {
      setError('请输入用户UUID')
      return
    }

    // 验证UUID格式
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidPattern.test(uuid.trim())) {
      setError('UUID格式不正确')
      return
    }

    setIsLoading(true)
    setError(null)

    // 验证用户是否存在
    const result = await verifyUser(uuid.trim())
    setIsLoading(false)

    if (result.success && result.data?.exists) {
      onSuccess({
        userUuid: uuid.trim()
      })
    } else {
      setError('该UUID不存在或无效')
    }
  }

  // 找回账号
  const handleForgotAccount = async () => {
    if (!phone || !smsCode) {
      setError('请填写手机号和验证码')
      return
    }

    setIsLoading(true)
    setError(null)

    const result = await forgotAccount({ phone, code: smsCode })
    setIsLoading(false)

    if (result.success && result.data) {
      setFoundAccount(result.data)
      setSuccess('找回成功！')
    } else {
      setError(result.message)
    }
  }

  // 复制UUID
  const handleCopyUuid = (text: string) => {
    navigator.clipboard.writeText(text)
    setSuccess('已复制到剪贴板')
    setTimeout(() => setSuccess(null), 2000)
  }

  if (!isOpen) return null

  // 获取标题
  const getTitle = () => {
    if (title) return title
    switch (mode) {
      case 'login': return '登录'
      case 'register': return '注册新账号'
      case 'sms-login': return '短信登录'
      case 'find-uuid': return '找回账号'
      case 'add-existing': return '添加已有账号'
      case 'forgot-account': return '找回账号'
      default: return '认证'
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 背景遮罩 */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* 弹窗内容 */}
      <div className="relative bg-white rounded-[32px] w-full max-w-md mx-4 shadow-2xl animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
        {/* 头部装饰 */}
        <div className="h-2 bg-gradient-to-r from-pink-400 via-red-400 to-orange-400" />
        
        {/* 头部 */}
        <div className="px-8 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {mode !== 'login' && mode !== 'add-existing' && (
              <button
                onClick={() => setMode('login')}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-500" />
              </button>
            )}
            <h2 className="text-xl font-black text-gray-800">{getTitle()}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* 内容区 */}
        <div className="px-8 pb-8">
          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* 成功提示 */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-xl flex items-center gap-2 text-green-600 text-sm">
              <Check size={16} />
              {success}
            </div>
          )}

          {/* 登录表单 */}
          {mode === 'login' && (
            <div className="space-y-4">
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="用户名或手机号"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                />
              </div>
              
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Smartphone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="验证码"
                    maxLength={6}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  />
                </div>
                <button
                  onClick={handleSendSms}
                  disabled={countdown > 0 || isLoading || !/^1[3-9]\d{9}$/.test(username)}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  {countdown > 0 ? `${countdown}s` : '获取验证码'}
                </button>
              </div>

              <p className="text-xs text-gray-500">
                {/^1[3-9]\d{9}$/.test(username) ? '已识别为手机号，可发送验证码' : '输入手机号可发送验证码'}
              </p>

              <button
                onClick={handleLogin}
                disabled={isLoading}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? <Loader2 size={20} className="animate-spin" /> : null}
                登录
              </button>

              {/* 其他选项 */}
              <div className="flex items-center justify-between text-sm">
                <button
                  onClick={() => setMode('sms-login')}
                  className="text-pink-500 hover:text-pink-600 font-medium"
                >
                  短信验证码登录
                </button>
                <button
                  onClick={() => setMode('forgot-account')}
                  className="text-gray-500 hover:text-gray-600"
                >
                  忘记账号？
                </button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-400">或者</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setMode('register')}
                  className="py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all"
                >
                  注册新账号
                </button>
                <button
                  onClick={() => setMode('add-existing')}
                  className="py-3 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl font-medium transition-all"
                >
                  添加已有账号
                </button>
              </div>

              <button
                onClick={() => setMode('find-uuid')}
                className="w-full py-2 text-gray-500 hover:text-pink-500 text-sm transition-colors"
              >
                通过手机号找回账号
              </button>
            </div>
          )}

          {/* 注册表单 */}
          {mode === 'register' && (
            <div className="space-y-4">
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="用户名（3-20个字符）"
                  className="w-full pl-12 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                />
                {usernameChecking && (
                  <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
                )}
                {!usernameChecking && usernameAvailable === true && (
                  <Check size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" />
                )}
                {!usernameChecking && usernameAvailable === false && (
                  <AlertCircle size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-red-500" />
                )}
              </div>

              <div className="relative">
                <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  placeholder="手机号"
                  maxLength={11}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                />
              </div>

              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Smartphone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="验证码"
                    maxLength={6}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                  />
                </div>
                <button
                  onClick={handleSendSms}
                  disabled={countdown > 0 || isLoading || !phone || !/^1[3-9]\d{9}$/.test(phone)}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  {countdown > 0 ? `${countdown}s` : '获取验证码'}
                </button>
              </div>

              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="昵称（可选）"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                />
              </div>

              <button
                onClick={handleRegister}
                disabled={isLoading || usernameAvailable === false}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? <Loader2 size={20} className="animate-spin" /> : null}
                注册
              </button>
            </div>
          )}

          {/* 短信登录表单 */}
          {mode === 'sms-login' && (
            <div className="space-y-4">
              <div className="relative">
                <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="手机号"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                />
              </div>

              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Smartphone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    placeholder="验证码"
                    maxLength={6}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                    onKeyDown={(e) => e.key === 'Enter' && handleSmsLogin()}
                  />
                </div>
                <button
                  onClick={handleSendSms}
                  disabled={countdown > 0 || isLoading}
                  className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  {countdown > 0 ? `${countdown}s` : '获取验证码'}
                </button>
              </div>

              <button
                onClick={handleSmsLogin}
                disabled={isLoading}
                className="w-full py-3 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? <Loader2 size={20} className="animate-spin" /> : null}
                登录 / 注册
              </button>

              <p className="text-center text-sm text-gray-500">
                未注册的手机号将自动创建新账号
              </p>
            </div>
          )}

          {/* 找回UUID表单 */}
          {mode === 'find-uuid' && (
            <div className="space-y-4">
              {!foundAccount ? (
                <>
                  <div className="relative">
                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="已绑定的手机号"
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                    />
                  </div>

                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <Smartphone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value)}
                        placeholder="验证码"
                        maxLength={6}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                        onKeyDown={(e) => e.key === 'Enter' && handleFindUuid()}
                      />
                    </div>
                    <button
                      onClick={handleSendSms}
                      disabled={countdown > 0 || isLoading}
                      className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all disabled:opacity-50 whitespace-nowrap"
                    >
                      {countdown > 0 ? `${countdown}s` : '获取验证码'}
                    </button>
                  </div>

                  <button
                    onClick={handleFindUuid}
                    disabled={isLoading}
                    className="w-full py-3 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isLoading ? <Loader2 size={20} className="animate-spin" /> : null}
                    找回账号
                  </button>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white">
                        <Check size={24} />
                      </div>
                      <div>
                        <div className="font-bold text-gray-800">找到账号</div>
                        <div className="text-sm text-gray-500">{foundAccount.nickname || foundAccount.username || '未命名用户'}</div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-3 flex items-center justify-between">
                      <code className="text-sm font-mono text-gray-700 break-all">
                        {foundAccount.userUuid}
                      </code>
                      <button
                        onClick={() => handleCopyUuid(foundAccount.userUuid)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleUseFoundAccount}
                    className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-bold transition-all"
                  >
                    使用此账号
                  </button>

                  <button
                    onClick={() => setFoundAccount(null)}
                    className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
                  >
                    查找其他账号
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 添加已有账号表单 */}
          {mode === 'add-existing' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
                <p className="font-medium mb-1">💡 提示</p>
                <p>如果你已有账号的 UUID，可以直接输入添加。UUID 是一串类似 <code className="bg-blue-100 px-1 rounded">xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx</code> 的字符串。</p>
              </div>

              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={uuid}
                  onChange={(e) => setUuid(e.target.value)}
                  placeholder="输入用户 UUID"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all font-mono text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddExisting()}
                />
              </div>

              <button
                onClick={handleAddExisting}
                disabled={isLoading}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? <Loader2 size={20} className="animate-spin" /> : null}
                添加账号
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-400">没有 UUID？</span>
                </div>
              </div>

              <button
                onClick={() => setMode('find-uuid')}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all"
              >
                通过手机号找回
              </button>
            </div>
          )}

          {/* 找回账号表单 */}
          {mode === 'forgot-account' && (
            <div className="space-y-4">
              {!foundAccount ? (
                <>
                  <div className="relative">
                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                      placeholder="已绑定的手机号"
                      maxLength={11}
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                    />
                  </div>

                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <Smartphone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="验证码"
                        maxLength={6}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none transition-all"
                        onKeyDown={(e) => e.key === 'Enter' && handleForgotAccount()}
                      />
                    </div>
                    <button
                      onClick={handleSendSms}
                      disabled={countdown > 0 || isLoading}
                      className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all disabled:opacity-50 whitespace-nowrap"
                    >
                      {countdown > 0 ? `${countdown}s` : '获取验证码'}
                    </button>
                  </div>

                  <button
                    onClick={handleForgotAccount}
                    disabled={isLoading}
                    className="w-full py-3 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isLoading ? <Loader2 size={20} className="animate-spin" /> : null}
                    找回账号
                  </button>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white">
                        <Check size={24} />
                      </div>
                      <div>
                        <div className="font-bold text-gray-800">找到账号</div>
                        <div className="text-sm text-gray-500">{foundAccount.nickname || foundAccount.username || '未命名用户'}</div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-3 flex items-center justify-between">
                      <code className="text-sm font-mono text-gray-700 break-all">
                        {foundAccount.userUuid}
                      </code>
                      <button
                        onClick={() => handleCopyUuid(foundAccount.userUuid)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleUseFoundAccount}
                    className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-bold transition-all"
                  >
                    使用此账号
                  </button>

                  <button
                    onClick={() => setFoundAccount(null)}
                    className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
                  >
                    查找其他账号
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 备案号 */}
        <div className="px-8 pb-6 pt-2 text-center text-xs text-gray-400 border-t border-gray-100 mt-2">
          <a
            href="https://beian.miit.gov.cn"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-600 transition-colors"
          >
            蜀ICP备2020028680号-1
          </a>
        </div>
      </div>
    </div>
  )
}

export default AuthDialog
